import { SvgIcon, Tooltip, Typography, useMediaQuery } from "@mui/material";
import LoginStyles from "./Login.styles";
import { descriptionFont, theme } from "../../utils/styles";
import { Box, Container } from "@mui/system";
import strings from "../../global/constants/StringConstants";
import urls from "../../global/constants/UrlConstants";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { loginAction, selectAuthenticated, selectLocale } from "../../redux/authSlice";
import HomeRunLogo from "../../assets/images/logos/logo-light-en.svg";
import HomeRunLogoFr from "../../assets/images/logos/logo-light-fr.svg";
import history from "../../utils/history";
import AppButton from "../../global/components/AppButton/AppButton";
import AppTextField from "../../global/components/AppTextField/AppTextField";
import { getBodyHeight, isTruthy, openErrorNotification } from "../../helpers/methods";
import AppFooter from "../Shared/AppFooter/AppFooter";
import sideImage from "../../assets/images/login-view-building.svg";
import { useState } from "react";
import CustomLoader from "../../global/components/CustomLoader/CustomLoader";
import notifiers from "../../global/constants/NotificationConstants";
import { verifyMFA } from "./LoginPageService";
import { mfaValidation } from "./LoginTypesAndValidation";
import { staticContent } from "../../utils/locale";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorModal from "./ErrorModal";
import {jwtDecode} from "jwt-decode";

const VerifyMFA = (props: any) => {
  const classes = LoginStyles;
  const userName = props.location.state?.userName;
  const redirectUrl = props.location.state?.redirectUrl;
  const urlParams = new URLSearchParams(redirectUrl.split("?")[1]);
  const token = urlParams.get("token");
  const email = urlParams.get("email");
  const phoneNumber = urlParams.get("phoneNumber");
  const verificationType = urlParams.get("verificationType");
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const locale = useAppSelector(selectLocale);
  const [isLoading, setIsLoading] = useState(false);
  const [formFields, setFormFields] = useState<any>({
    mfa: {
      value: "",
      error: "",
    },
  });
  const [errorModal, setErrorModal] = useState({ open: false, message: "" });
  const authenticated = useAppSelector(selectAuthenticated);
  const dispatch = useAppDispatch();
  const text = staticContent(locale, authenticated);

  const verifyMFAHandler = async () => {
    try {
      if (handleValidation()) {
        setIsLoading(true);
        const user = await verifyMFA(
          userName,
          token!,
          verificationType!,
          formFields.mfa.value,
          locale,
        );
        const decodedIdToken: any = jwtDecode(user.authToken);
        dispatch(
          loginAction({
            email,
            authenticated: true,
            accessToken: user.authToken,
            refreshToken: user.refreshToken,
            userName: user.userName,
            role: props.adminLogin ? "cms" : "coop",
            coopName: props.adminLogin ? "" : decodedIdToken.coopReportName,
            city: props.adminLogin ? "" : user.city,
            province: props.adminLogin ? "" : user.province,
          }),
        );
        history.push(props.adminLogin ? urls.adminPath : urls.homeViewPath);
      }
    } catch (error: any) {
      setIsLoading(false);
      setErrorModal({
        open: true,
        message: error.errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleOnChangeInputField = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    let onlyNumbers = value.replace(/[^0-9]/g, "");

    setFormFields((prev: any) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: onlyNumbers,
        error: "",
      },
    }));
  };

  const handleValidation = () => {
    const { isValid, errors } = mfaValidation(formFields, text);
    setFormFields({ ...errors });
    return isValid;
  };

  const getVerifyMFACard = () => {
    return (
      <Box sx={classes.backgroundBox}>
        <Container maxWidth="xl">
          <Box sx={[classes.wrapper, { height: { lg: `calc(85vh - ${getBodyHeight()}px)` } }]}>
            <Box sx={classes.cardWrapper}>
              <Box sx={classes.loginCard}>
                <Typography color="#9e9bb4" fontWeight={600}>
                  {locale === "en"
                    ? "Multi-Factor Authentication"
                    : "Authentification multifacteur"}
                </Typography>
                <Box>
                  <Typography sx={classes.title} variant="h5" fontWeight={700} display="inline">
                    <Typography
                      sx={classes.title}
                      variant="h5"
                      fontWeight={700}
                      color="secondary"
                      display="inline"
                    >
                      {locale === "en" ? "Verify your identity" : "Vérifier votre identité"}
                    </Typography>{" "}
                  </Typography>
                  <Typography
                    fontSize={18}
                    fontWeight={500}
                    variant="subtitle1"
                    sx={{ ...descriptionFont }}
                    pt={1}
                  >
                    {locale === "en" ? (
                      <>
                        Your one-time security code was sent by{" "}
                        {phoneNumber ? "text message" : "email"} to <br />
                        <Typography component="span" fontWeight={600} pt={1}>
                          {phoneNumber ? phoneNumber : email}
                        </Typography>
                        <br />
                        The code will expire in a few minutes.
                      </>
                    ) : (
                      <>
                        {phoneNumber ? (
                          <>
                            Votre code de sécurité à utilisation unique a été envoyé par téléphone
                            à{" "}
                            <Typography component="span" fontWeight={600}>
                              {phoneNumber}
                            </Typography>
                          </>
                        ) : (
                          <>
                            Votre code de sécurité à utilisation unique a été envoyé par courriel à{" "}
                            <br />
                            <Typography component="span" fontWeight={600}>
                              {email}
                            </Typography>
                          </>
                        )}
                        <br />
                        Le code expirera dans quelques minutes.
                      </>
                    )}
                  </Typography>
                </Box>
                <Box display="flex" gap={2} flexDirection="column" width="100%">
                  <AppTextField
                    fieldLabel={
                      locale === "en" ? `Enter Security Code` : `Saisir le code de sécurité`
                    }
                    placeholder={locale === "en" ? "Type code here" : "Saisir le code ici"}
                    onKeyPress={() => ""}
                    onChange={handleOnChangeInputField}
                    value={formFields.mfa.value}
                    fieldId="mfa"
                    fieldName="mfa"
                    required
                    fullWidth
                    type="text"
                    removeUpDownIcon
                    error={!isTruthy(formFields.mfa.value) && formFields.mfa.error}
                  />
                  {/* <Box>
                    <Typography
                      my={1}
                      fontSize={16}
                      sx={{ ...descriptionFont }}
                      variant="subtitle1"
                    >
                      {parse(markdownText(content.description))}
                    </Typography>
                  </Box> */}
                </Box>
                <Box mt={2} display="flex" flexDirection="column" width="100%">
                  <AppButton
                    label={locale === "en" ? "VERIFY" : "VÉRIFIER"}
                    withRadius
                    styles={{
                      alignSelf: "center",
                      fontSize: 14,
                      paddingBottom: `8px`,
                      paddingTop: `8px`,
                      fontWeight: 400,
                      width: "100%",
                    }}
                    onClick={() => verifyMFAHandler()}
                  />

                  <Box mt={2} width="fit-content">
                    <Typography
                      fontSize={14}
                      sx={{
                        cursor: "pointer",
                        color: "#319ca7",
                        fontWeight: 600,
                      }}
                      onClick={() => history.push(urls.loginViewPath)}
                    >
                      {locale === "en" ? "Back to Login" : "Retour à la page de connexion"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* <Typography sx={{ mt: 4, textAlign: "center", fontSize: 14 }}>
                © 2023 HomeRun. All Rights Reserved
              </Typography> */}
            </Box>
          </Box>
        </Container>
      </Box>
    );
  };

  return (
    <>
      <Box sx={classes.header}>
        <Container maxWidth="xl">
          <img
            src={locale === strings.EN ? HomeRunLogo : HomeRunLogoFr}
            alt="Homerun"
            height="80px"
            style={{ cursor: "pointer" }}
            onClick={() => history.push(urls.homeViewPath)}
          />
        </Container>
      </Box>
      {getVerifyMFACard()}
      <AppFooter />
      {isDesktop && (
        <img
          src={sideImage}
          style={{
            position: "fixed",
            right: "-15%",
            top: 0,
            width: "70%",
            overflow: "hidden",
            objectFit: "fill",
            zIndex: 5,
            height: `calc(100vh - ${getBodyHeight()}px)`,
          }}
          alt=""
        />
      )}
      {errorModal.open && (
        <ErrorModal
          open={errorModal.open}
          onClose={() => setErrorModal({ open: false, message: "" })}
          message={errorModal.message}
          locale={locale}
        />
      )}
      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default VerifyMFA;
